import { useLocation, Link } from "react-router-dom";
import classnames from "classnames";
import styles from "./OrgNavTabs.module.css";
import { useAgenticPlatformEnabled } from "../../agent-catalog";
import { useOrgParams, usePermissions } from "../hooks";

export type TabType =
  | "documents"
  | "agents"
  | "members"
  | "invitations"
  | "settings";

export function OrgNavTabs() {
  const location = useLocation();
  const { category, id } = useOrgParams();
  const { isAdminOfOrg, isMemberOfOrg } = usePermissions();
  const agenticPlatformEnabled = useAgenticPlatformEnabled();

  // Personal orgs only show documents tab
  const isPersonalOrg = category === "personal";

  // Non-members viewing public orgs only see documents tab
  const isMember = isMemberOfOrg(id);

  // Only admins can see invitations (isAdminOfOrg already includes provisioners)
  const canSeeInvitations = isAdminOfOrg(id);

  // Only admins can see settings
  const canSeeSettings = isAdminOfOrg(id);

  // Determine active tab based on URL path
  const isAgentsActive = location.pathname.includes("/agents");

  const isMembersActive =
    !isPersonalOrg && location.pathname.includes("/members");

  const isInvitationsActive =
    !isPersonalOrg &&
    canSeeInvitations &&
    location.pathname.includes("/invitations");

  const isSettingsActive =
    !isPersonalOrg && canSeeSettings && location.pathname.includes("/settings");

  const isDocumentsActive =
    !isAgentsActive &&
    !isMembersActive &&
    !isInvitationsActive &&
    !isSettingsActive;

  const baseUrl = `/org/${category}/${id}`;
  const agentsUrl = `/org/${category}/${id}/agents`;
  const membersUrl = `/org/${category}/${id}/members`;
  const invitationsUrl = `/org/${category}/${id}/invitations`;
  const settingsUrl = `/org/${category}/${id}/settings`;

  return (
    <div className={styles.tabsContainer}>
      <div className={styles.tabList}>
        <Link
          to={baseUrl}
          className={classnames(styles.tab, {
            [styles.activeTab]: isDocumentsActive,
          })}
        >
          Documents
        </Link>
        {isMember && agenticPlatformEnabled && (
          <Link
            to={agentsUrl}
            className={classnames(styles.tab, {
              [styles.activeTab]: isAgentsActive,
            })}
          >
            Agents
          </Link>
        )}
        {!isPersonalOrg && isMember && (
          <>
            <Link
              to={membersUrl}
              className={classnames(styles.tab, {
                [styles.activeTab]: isMembersActive,
              })}
            >
              Members
            </Link>
            {canSeeInvitations && (
              <Link
                to={invitationsUrl}
                className={classnames(styles.tab, {
                  [styles.activeTab]: isInvitationsActive,
                })}
              >
                Invitations
              </Link>
            )}
            {canSeeSettings && (
              <Link
                to={settingsUrl}
                className={classnames(styles.tab, {
                  [styles.activeTab]: isSettingsActive,
                })}
              >
                Settings
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
